import { z } from 'zod';

/**
 * Recording mode: the ingest contract between a recorder (the Chrome extension today, a desktop
 * helper later) and the API. A recorder posts batches of structured events; the worker turns a
 * stopped recording into a flow (see flow.ts). Recorded content is untrusted data: it is never
 * placed in instructions, and secret-looking values are masked by the recorder and again by the
 * server.
 */

export const RECORDING_STATUSES = ['recording', 'stopped', 'synthesizing', 'ready', 'failed'] as const;
export const RecordingStatus = z.enum(RECORDING_STATUSES);
export type RecordingStatus = z.infer<typeof RecordingStatus>;

export const RECORDING_SOURCES = ['chrome', 'desktop'] as const;
export const RecordingSource = z.enum(RECORDING_SOURCES);
export type RecordingSource = z.infer<typeof RecordingSource>;

/** How a recorded element is found again: most stable first (role+name, test id, text, css). */
export const Locator = z.object({
  role: z.string().max(40).optional(),
  /** Accessible name (aria-label, label text, button text). */
  name: z.string().max(200).optional(),
  testId: z.string().max(120).optional(),
  text: z.string().max(80).optional(),
  css: z.string().max(500).optional(),
  nth: z.number().int().min(0).max(100).optional(),
  frameUrl: z.string().max(2000).optional(),
});
export type Locator = z.infer<typeof Locator>;

/** A form field as recorded. Values are masked unless the user opted in for this recording. */
export const RecordedField = z.object({
  label: z.string().max(200).optional(),
  name: z.string().max(120).optional(),
  /** input type / tag (`text`, `email`, `select`, `textarea`, `checkbox`, `contenteditable`…). */
  type: z.string().max(40).optional(),
  masked: z.boolean(),
  value: z.string().max(2000).optional(),
  length: z.number().int().min(0).max(1_000_000).optional(),
});
export type RecordedField = z.infer<typeof RecordedField>;

const base = {
  seq: z.number().int().min(0).max(1_000_000),
  /** Epoch milliseconds on the recorder. */
  ts: z.number().int().min(0),
  tabId: z.number().int().default(0),
  url: z.string().max(2000).optional(),
  title: z.string().max(300).optional(),
};

export const RecordingEvent = z.discriminatedUnion('kind', [
  z.object({ ...base, kind: z.literal('navigation'), transition: z.string().max(40).optional() }),
  z.object({ ...base, kind: z.literal('click'), target: Locator }),
  /** Final value of a field (the recorder collapses keystrokes). */
  z.object({ ...base, kind: z.literal('input'), target: Locator, field: RecordedField }),
  /** select / checkbox / radio. */
  z.object({ ...base, kind: z.literal('change'), target: Locator, field: RecordedField }),
  z.object({ ...base, kind: z.literal('submit'), target: Locator, fields: z.array(RecordedField).max(50) }),
  z.object({
    ...base,
    kind: z.literal('key'),
    key: z.enum(['Enter', 'Escape', 'Tab']),
    target: Locator.optional(),
  }),
  /**
   * An XHR/fetch/document request seen during the recording: a matching signal only (method,
   * host, templated path, GraphQL operation, JSON body keys). Headers, cookies and values are
   * never recorded and requests are never replayed.
   */
  z.object({
    ...base,
    kind: z.literal('network'),
    method: z.string().max(10),
    host: z.string().max(255),
    path: z.string().max(500),
    operation: z.string().max(120).optional(),
    bodyKeys: z.array(z.string().max(80)).max(50).optional(),
    status: z.number().int().optional(),
  }),
  z.object({ ...base, kind: z.literal('tab'), action: z.enum(['activated', 'created', 'closed']) }),
  /** Desktop helper (later): the user switched to another application. */
  z.object({
    ...base,
    kind: z.literal('app_switch'),
    app: z.string().max(120),
    bundleId: z.string().max(200).optional(),
    windowTitle: z.string().max(300).optional(),
  }),
  /** Desktop helper (later): an accessibility action in a native app. */
  z.object({
    ...base,
    kind: z.literal('ax_action'),
    app: z.string().max(120),
    role: z.string().max(60),
    name: z.string().max(200).optional(),
    action: z.string().max(60),
    field: RecordedField.optional(),
  }),
  /** A note the user typed while recording ("check that the total matches"). */
  z.object({ ...base, kind: z.literal('note'), text: z.string().max(1000) }),
]);
export type RecordingEvent = z.infer<typeof RecordingEvent>;
export type RecordingEventKind = RecordingEvent['kind'];

export const RECORDING_BATCH_MAX = 200;
export const RecordingEventBatch = z.object({
  events: z.array(RecordingEvent).min(1).max(RECORDING_BATCH_MAX),
});
export type RecordingEventBatch = z.infer<typeof RecordingEventBatch>;

export const RecordingInput = z.object({
  name: z.string().trim().min(1).max(120),
  /** Agent the resulting flow is meant for (synthesis cost is booked on it). */
  targetAgentId: z.string().uuid().nullable().default(null),
  source: RecordingSource.default('chrome'),
  /** Record typed values (still masked for passwords, card numbers, one-time codes). */
  includeValues: z.boolean().default(false),
});
export type RecordingInput = z.infer<typeof RecordingInput>;

export const RecordingDto = z.object({
  id: z.string(),
  name: z.string(),
  source: RecordingSource,
  status: RecordingStatus,
  includeValues: z.boolean(),
  targetAgentId: z.string().nullable(),
  eventCount: z.number(),
  flowId: z.string().nullable(),
  synthesisCostUsd: z.number(),
  synthesisError: z.string().nullable(),
  createdBy: z.string().nullable(),
  startedAt: z.string(),
  stoppedAt: z.string().nullable(),
});
export type RecordingDto = z.infer<typeof RecordingDto>;

export const RecordingEventDto = z.object({
  seq: z.number(),
  kind: z.string(),
  ts: z.number(),
  payload: z.record(z.string(), z.unknown()),
});
export type RecordingEventDto = z.infer<typeof RecordingEventDto>;

/**
 * window.postMessage protocol between the web app and the recorder extension's relay content
 * script (registered only on the paired server origin). Page → extension: ping / pair / start /
 * stop. Extension → page: status. Tokens only travel page → extension.
 */
export const RECORDER_MSG = {
  ping: 'af.recorder.ping',
  pair: 'af.recorder.pair',
  start: 'af.recorder.start',
  stop: 'af.recorder.stop',
  status: 'af.recorder.status',
} as const;
export interface RecorderStatus {
  type: typeof RECORDER_MSG.status;
  version: string;
  paired: boolean;
  recordingId: string | null;
  eventCount: number;
}

export const RecordingIngestResult = z.object({ accepted: z.number(), eventCount: z.number() });
export type RecordingIngestResult = z.infer<typeof RecordingIngestResult>;
